/**
 * Top-level editor page mounted at /research/new and /research/:slug/edit.
 *
 * - "new" mode: blank fields, first save POSTs and we then navigate to the
 *   newly assigned slug's /edit URL.
 * - "edit" mode: loads the article via the existing useArticle pattern,
 *   tracks expected_version for optimistic locking.
 *
 * Layout matches the rest of the research routes — same `px: 2` content
 * indent as ArticleDetail so the editor sits in the same horizontal
 * channel as the read view.
 */

import React, { useCallback, useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import {
  Alert,
  Box,
  Button,
  Chip,
  CircularProgress,
  IconButton,
  MenuItem,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import CloseIcon from "@mui/icons-material/Close";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import HistoryIcon from "@mui/icons-material/History";
import PublishIcon from "@mui/icons-material/Publish";
import SaveIcon from "@mui/icons-material/Save";
import UnpublishedIcon from "@mui/icons-material/Unpublished";

import { useResearchLayoutContext } from "../ResearchLayout";
import { useLeagueApi } from "../../../../data/LeagueEnvironmentProvider";
import {
  ResearchArticleDetail,
  ResearchArticleVisibility,
  UpdateResearchArticleRequest,
} from "../../../../models/Research";
import EmbeddedQueriesList from "./EmbeddedQueriesList";
import MarkdownEditor from "./MarkdownEditor";
import PendingEmbedsList from "./PendingEmbedsList";
import PendingRelatedList from "./PendingRelatedList";
import RelatedArticlesEditor from "./RelatedArticlesEditor";
import DraftVisibilityToggle from "./DraftVisibilityToggle";
import VersionHistoryDrawer from "./VersionHistoryDrawer";
import { useArticleEditor } from "./useArticleEditor";
import ArticleDetailSkeleton from "../ArticleDetailSkeleton";

// Matches ArticleDetail's outer padding so editor and read view align.
const EDITOR_SX = { px: 2 };

// Shared style for top-bar control buttons. Keeps them as a consistent
// horizontal row that matches the app-wide toolbar pattern in CLAUDE.md.
const CONTROL_BUTTON_SX = {
  textTransform: "none" as const,
  fontSize: "0.75rem",
  fontWeight: 600,
  letterSpacing: "0.2px",
  whiteSpace: "nowrap" as const,
  minWidth: 0,
};

// Save button — matches the app-wide dark header / active-chip pattern
// (#4c4c4c, white text). Trail Blazers red is reserved for brand accents
// (active category row, links); a contained red button reads as alarming.
const PRIMARY_BUTTON_SX = {
  ...CONTROL_BUTTON_SX,
  backgroundColor: "#4c4c4c",
  color: "#fff",
  boxShadow: "none",
  "&:hover": { backgroundColor: "#3a3a3a", boxShadow: "none" },
  "&.Mui-disabled": {
    backgroundColor: "#bdbdbd",
    color: "#fff",
  },
};

// Subtle light-fill surface used for the metadata block. Matches the
// CategoryRail sidebar so the two read as the same family of "chrome".
const METADATA_CARD_SX = {
  backgroundColor: "#fafafa",
  border: "1px solid #e6e6e6",
  borderRadius: "4px",
  p: 2.5,
  mb: 3,
};

// Input override: white fill against the gray card so each field reads
// as a distinct surface. Used on title / summary / category.
const INPUT_SX = {
  "& .MuiOutlinedInput-root": { backgroundColor: "#fff" },
};

const SECTION_LABEL_SX = {
  display: "block",
  fontSize: "0.72rem",
  fontWeight: 700,
  letterSpacing: "0.5px",
  textTransform: "uppercase" as const,
  color: "text.secondary",
  mb: 0.5,
};

const FIELD_HELPER_SX = {
  display: "block",
  fontSize: "0.75rem",
  color: "text.secondary",
  mt: 0.5,
};

function extractAxiosError(err: unknown, fallback: string): string {
  if (axios.isAxiosError(err)) {
    return (
      err.response?.data?.error ||
      err.response?.data?.detail ||
      err.message ||
      fallback
    );
  }
  if (err instanceof Error) return err.message;
  return fallback;
}

export default function ArticleEditor() {
  const { slug } = useParams<{ slug: string }>();
  const isExisting = Boolean(slug);
  const navigate = useNavigate();
  const api = useLeagueApi();
  const { categories, refetchIndex } = useResearchLayoutContext();

  const editor = useArticleEditor(isExisting);
  const { state } = editor;
  const [versionDrawerOpen, setVersionDrawerOpen] = React.useState(false);

  // ---- Sticky action toolbar ----
  //
  // The toolbar pins at the top of the viewport when scrolled past so the
  // Save / Publish / Delete actions stay reachable during long-body edits.
  // Mirrors the article-detail page's sticky header pattern: zero-height
  // sentinel + IntersectionObserver for the "is stuck" elevation cue,
  // ::before pseudo-shield to cover the 24px gap above the bar on md+
  // (aligns the action bar with the sticky category rail).
  //
  // The header's measured height is fed to MarkdownEditor as
  // `stickyToolbarTop` so the editor's helper toolbar (bold/italic/table)
  // can stick *directly below* the action bar without overlapping it.
  // ResizeObserver keeps the offset accurate when the action bar wraps
  // onto a second row at narrow widths.
  const theme = useTheme();
  const isMdUp = useMediaQuery(theme.breakpoints.up("md"));
  const [headerHeight, setHeaderHeight] = useState(0);
  const [headerStuck, setHeaderStuck] = useState(false);

  // Callback refs (not useRef + useEffect) because this component renders
  // a skeleton first while loading an existing article, then swaps to the
  // real UI. A useRef-based observer setup attached in `useEffect(..., [])`
  // would run once at skeleton-mount with `ref.current === null`, return
  // early, and never re-run when the real action bar appeared. Callback
  // refs fire whenever the node attaches/detaches from the DOM, so the
  // observers wire up the moment the bar exists.
  //
  // Each callback holds its own ResizeObserver / IntersectionObserver in
  // a ref so the cleanup branch (`node === null`) can disconnect it. The
  // sentinel callback depends on `isMdUp` because the IO `rootMargin`
  // varies with the bar's sticky offset.
  const headerObserverRef = useRef<ResizeObserver | null>(null);
  const sentinelObserverRef = useRef<IntersectionObserver | null>(null);

  const headerRef = useCallback((node: HTMLDivElement | null) => {
    if (headerObserverRef.current) {
      headerObserverRef.current.disconnect();
      headerObserverRef.current = null;
    }
    if (!node) return;
    const ro = new ResizeObserver(([entry]) => {
      // Border-box height — content box excludes the bar's pt/pb padding
      // and 1px bottom border (~25px). Using contentRect would pin the
      // helper toolbar that much too high, hiding it behind the action
      // bar. `borderBoxSize[0].blockSize` is supported in all current
      // browsers; fall back to `offsetHeight` (also border-box) otherwise.
      const h =
        entry.borderBoxSize?.[0]?.blockSize ??
        (entry.target as HTMLElement).offsetHeight;
      setHeaderHeight(h);
    });
    ro.observe(node);
    headerObserverRef.current = ro;
  }, []);

  const headerSentinelRef = useCallback(
    (node: HTMLDivElement | null) => {
      if (sentinelObserverRef.current) {
        sentinelObserverRef.current.disconnect();
        sentinelObserverRef.current = null;
      }
      if (!node) return;
      const stickyTop = isMdUp ? 24 : 0;
      const io = new IntersectionObserver(
        ([entry]) => setHeaderStuck(!entry.isIntersecting),
        // Shrink the IO viewport top by the bar's sticky offset so the
        // sentinel exits the IO viewport at the exact moment the bar
        // becomes visually stuck.
        { rootMargin: `-${stickyTop}px 0px 0px 0px`, threshold: [0] },
      );
      io.observe(node);
      sentinelObserverRef.current = io;
    },
    [isMdUp],
  );

  // Disconnect both observers on unmount in case the component goes away
  // while a node is still attached (the callback ref's cleanup branch
  // only fires when the node leaves the DOM, not when the component
  // unmounts with the node still mounted to it).
  useEffect(() => {
    return () => {
      headerObserverRef.current?.disconnect();
      sentinelObserverRef.current?.disconnect();
    };
  }, []);

  const helperStickyTop = isMdUp ? 24 + headerHeight : headerHeight;

  // ---- Load existing article ----
  useEffect(() => {
    if (!isExisting || !slug) return;
    let cancelled = false;
    api
      .getResearchArticle(slug)
      .then((resp) => {
        if (cancelled) return;
        editor.onLoaded(resp.data);
      })
      .catch((err) => {
        if (cancelled) return;
        editor.onLoadError(extractAxiosError(err, "Failed to load article"));
      });
    return () => {
      cancelled = true;
    };
    // editor.onLoaded / onLoadError are stable (wrapped in useCallback)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [api, isExisting, slug]);

  // ---- Submission helpers ----
  const buildUpdateBody = (): UpdateResearchArticleRequest => ({
    expected_version: state.expectedVersion,
    change_note: state.changeNote || undefined,
    title: state.fields.title,
    summary: state.fields.summary,
    category_id: state.fields.categoryId,
    visibility: state.fields.visibility,
    body_md: state.fields.bodyMd,
  });

  const handleSaveOkRedirect = (article: ResearchArticleDetail) => {
    editor.onSaveOk(article);
    // Refresh the layout's cached article index so a new article shows
    // up in the rail's "Recently Updated" and on the list page, and an
    // edited article picks up its new title / category / timestamp,
    // without requiring a full page reload.
    refetchIndex();
    // After Save (either create or update), drop the editor and jump to
    // the read view so the author sees what they just wrote. They can
    // re-enter the editor via the Edit button on the detail page.
    navigate(`/research/${article.slug}`, { replace: !isExisting });
  };

  const handleSaveError = (err: unknown, defaultMsg: string) => {
    if (axios.isAxiosError(err) && err.response?.status === 409) {
      const body = err.response.data || {};
      editor.onSaveConflict(
        body.error || "Article was updated by someone else",
        body.current_version ?? null,
      );
      return;
    }
    editor.onSaveError(extractAxiosError(err, defaultMsg));
  };

  const onSave = async () => {
    if (!state.fields.title.trim()) {
      editor.onSaveError("Title is required");
      return;
    }
    editor.beginSave();
    try {
      if (isExisting && slug) {
        const resp = await api.updateResearchArticle(slug, buildUpdateBody());
        handleSaveOkRedirect(resp.data);
      } else {
        // Ship pending embeds + related-article links with the create
        // POST so the new-article page is a one-step flow (no
        // save-then-edit dance). Backend creates everything atomically;
        // any bad embed / unresolvable related slug rolls the whole
        // article back. Empty arrays are fine — backend treats them as
        // "no pending list."
        const resp = await api.createResearchArticle({
          title: state.fields.title,
          summary: state.fields.summary || null,
          category_id: state.fields.categoryId,
          visibility: state.fields.visibility,
          body_md: state.fields.bodyMd,
          embedded_queries: state.pendingEmbeds.map((e) => ({
            saved_query_id: e.saved_query_id,
            title_override: e.title_override,
          })),
          related_slugs: state.pendingRelated.map((r) => r.slug),
        });
        handleSaveOkRedirect(resp.data);
      }
    } catch (err) {
      handleSaveError(err, "Failed to save");
    }
  };

  const onPublish = async () => {
    if (!isExisting || !slug) {
      editor.onSaveError("Save the draft once before publishing");
      return;
    }
    if (state.dirty) {
      editor.onSaveError("Save your changes before publishing");
      return;
    }
    editor.beginPublish();
    try {
      const resp = await api.publishResearchArticle(slug, {
        expected_version: state.expectedVersion,
      });
      editor.onSaveOk(resp.data);
      // Status changed (draft → published) — the rail's draft chip and
      // visibility for non-admins both depend on the index.
      refetchIndex();
    } catch (err) {
      handleSaveError(err, "Failed to publish");
    }
  };

  const onUnpublish = async () => {
    if (!isExisting || !slug) return;
    editor.beginUnpublish();
    try {
      const resp = await api.unpublishResearchArticle(slug, {
        expected_version: state.expectedVersion,
      });
      editor.onSaveOk(resp.data);
      refetchIndex();
    } catch (err) {
      handleSaveError(err, "Failed to unpublish");
    }
  };

  const onDelete = async () => {
    if (!isExisting || !slug) return;
    // Lightweight guardrail — replace with a styled modal in Phase 4 polish.
    // eslint-disable-next-line no-restricted-globals
    if (!confirm("Delete this article? This cannot be undone.")) return;
    editor.beginDelete();
    try {
      await api.deleteResearchArticle(slug);
      // Refresh before navigating so the list the user lands on
      // already reflects the deletion (no flash of the deleted row).
      refetchIndex();
      navigate("/research");
    } catch (err) {
      editor.onSaveError(extractAxiosError(err, "Failed to delete"));
    }
  };

  const onReloadAfterConflict = async () => {
    if (!isExisting || !slug) return;
    try {
      const resp = await api.getResearchArticle(slug);
      editor.onLoaded(resp.data);
    } catch (err) {
      editor.onSaveError(extractAxiosError(err, "Failed to reload"));
    }
  };

  // ---- Render ----

  if (isExisting && !state.loaded && state.status === "loading") {
    return <ArticleDetailSkeleton />;
  }

  if (isExisting && !state.loaded && state.error) {
    return (
      <Box sx={EDITOR_SX}>
        <Alert severity="error">{state.error}</Alert>
      </Box>
    );
  }

  const status = state.loaded?.status ?? "draft";
  const isDraft = status === "draft";
  const isBusy = state.status !== "idle";

  return (
    <Box sx={EDITOR_SX}>
      {/* Zero-height sentinel for IntersectionObserver — fires when the
          action toolbar becomes "stuck" so we can fade in the elevation cue. */}
      <Box ref={headerSentinelRef} aria-hidden sx={{ height: 0 }} />
      <Box
        ref={headerRef}
        sx={{
          // Sticks at top:24 on md+ (aligning with the rail) and top:0 on
          // xs (no rail to align with). zIndex 11 keeps it layered above
          // the helper toolbar's zIndex 10 when both are stuck.
          position: "sticky",
          top: { xs: 0, md: 24 },
          zIndex: 11,
          // Span the full column width — sit outside the page's px: 2 so
          // the bottom border + shadow read as architectural chrome, not
          // as a fenced box inside the content column.
          mx: -2,
          px: 2,
          pt: 1.5,
          pb: 1.5,
          mb: 2,
          // Always-opaque page bg color — same color as the page so the
          // bar is visually invisible at scroll=0. Transitioning bg from
          // transparent to opaque was creating a flash of content through
          // the bar on fast scrolls (see article-detail header notes).
          // Elevation cue lives in border + shadow, both paint-only.
          backgroundColor: "#f5f5f5",
          borderBottom: headerStuck
            ? "1px solid #e0e0e0"
            : "1px solid transparent",
          boxShadow: headerStuck ? "0 2px 8px rgba(0, 0, 0, 0.04)" : "none",
          transition: "border-color 180ms ease, box-shadow 180ms ease",
          "@media (prefers-reduced-motion: reduce)": { transition: "none" },
          // Shield: 24px tall pseudo above the bar (md+ only) covering the
          // gap between viewport top and the bar's sticky offset. Same bg
          // as the bar so the two form one continuous opaque strip when
          // stuck. On xs the bar sits at top:0 so no shield is needed.
          "&::before": {
            content: '""',
            position: "absolute",
            left: 0,
            right: 0,
            top: { xs: 0, md: -24 },
            height: { xs: 0, md: 24 },
            backgroundColor: "#f5f5f5",
            pointerEvents: "none",
          },
          display: "flex",
          flexDirection: { xs: "column", md: "row" },
          alignItems: { md: "center" },
          justifyContent: "space-between",
          gap: 1.5,
        }}
      >
        <Stack direction="row" spacing={1.25} alignItems="center" flexWrap="wrap">
          <Typography
            sx={{
              fontSize: "0.85rem",
              fontWeight: 700,
              letterSpacing: "0.6px",
              textTransform: "uppercase",
              color: "text.primary",
            }}
          >
            {isExisting ? "Edit Article" : "New Article"}
          </Typography>
          {isExisting && (
            <Chip
              label={isDraft ? `Draft · ${state.fields.visibility}` : "Published"}
              size="small"
              sx={{
                height: 22,
                borderRadius: "3px",
                fontSize: "0.7rem",
                fontWeight: 700,
                letterSpacing: "0.3px",
                backgroundColor: isDraft ? "#f0f0f0" : "#1b6e2c",
                color: isDraft ? "#666" : "#fff",
                textTransform: "uppercase",
              }}
            />
          )}
          {state.dirty && (
            <Typography
              sx={{
                color: "text.secondary",
                fontSize: "0.78rem",
                fontStyle: "italic",
              }}
            >
              unsaved changes
            </Typography>
          )}
        </Stack>

        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            alignItems: "center",
            gap: 1,
          }}
        >
          {isExisting && (
            <Tooltip title="Version history" arrow>
              <IconButton
                size="small"
                onClick={() => setVersionDrawerOpen(true)}
              >
                <HistoryIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
          <Button
            size="small"
            variant="outlined"
            startIcon={<CloseIcon />}
            onClick={() =>
              navigate(isExisting && slug ? `/research/${slug}` : "/research")
            }
            disabled={isBusy}
            sx={CONTROL_BUTTON_SX}
          >
            {state.dirty ? "Discard" : "Close"}
          </Button>
          {isExisting && isDraft && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<PublishIcon />}
              onClick={onPublish}
              disabled={isBusy || state.dirty}
              sx={CONTROL_BUTTON_SX}
            >
              {state.status === "publishing" ? "Publishing…" : "Publish"}
            </Button>
          )}
          {isExisting && !isDraft && (
            <Button
              size="small"
              variant="outlined"
              startIcon={<UnpublishedIcon />}
              onClick={onUnpublish}
              disabled={isBusy}
              sx={CONTROL_BUTTON_SX}
            >
              {state.status === "unpublishing" ? "Unpublishing…" : "Unpublish"}
            </Button>
          )}
          <Button
            size="small"
            variant="contained"
            startIcon={<SaveIcon />}
            onClick={onSave}
            // Gate on `state.dirty` — embed CRUD lives in
            // `EmbeddedQueriesList` and doesn't flow through the
            // editor's reducer, so a user who only changed embeds
            // would otherwise be able to click Save and trigger a
            // no-op article PUT that bumps `current_version` and
            // writes an empty version snapshot. The Save button is
            // strictly about the article body / metadata fields.
            disabled={isBusy || !state.dirty}
            sx={PRIMARY_BUTTON_SX}
          >
            {state.status === "saving" ? "Saving…" : "Save"}
          </Button>
          {isExisting && (
            <Tooltip title="Delete article" arrow>
              <IconButton
                size="small"
                onClick={onDelete}
                disabled={isBusy}
                sx={{ color: "#a62020" }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </Tooltip>
          )}
        </Box>
      </Box>

      {state.conflict && (
        <Alert
          severity="warning"
          sx={{ mb: 2 }}
          action={
            <Button
              size="small"
              onClick={onReloadAfterConflict}
              sx={{ textTransform: "none" }}
            >
              Reload
            </Button>
          }
        >
          {state.conflict.message}
          {state.conflict.currentVersion !== null
            ? ` (current v${state.conflict.currentVersion}).`
            : "."}{" "}
          Reloading replaces your unsaved edits with the latest version.
        </Alert>
      )}

      {state.error && !state.conflict && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {state.error}
        </Alert>
      )}

      <Box sx={METADATA_CARD_SX}>
        <Stack spacing={2.5}>
          <Box>
            <Typography sx={SECTION_LABEL_SX}>
              Title <span style={{ color: "#a62020" }}>*</span>
            </Typography>
            <TextField
              fullWidth
              size="small"
              placeholder="Article title"
              value={state.fields.title}
              onChange={(e) => editor.setField("title", e.target.value)}
              sx={{
                ...INPUT_SX,
                "& .MuiOutlinedInput-input": {
                  fontSize: "1.05rem",
                  fontWeight: 600,
                },
              }}
            />
          </Box>

          <Box>
            <Typography sx={SECTION_LABEL_SX}>Summary</Typography>
            {/*
              Plain styled textarea — MUI's outlined-multiline TextField
              kept rendering its internal padding in a way that left the
              first line of value text sitting on top of the section label.
              Naked <textarea> styled to match MUI's small-outlined inputs
              sidesteps the whole label/notch machinery.
            */}
            <Box
              component="textarea"
              rows={3}
              placeholder="One-paragraph overview shown on cards and at the top of the article."
              value={state.fields.summary}
              onChange={(e: React.ChangeEvent<HTMLTextAreaElement>) =>
                editor.setField("summary", e.target.value)
              }
              sx={{
                display: "block",
                width: "100%",
                boxSizing: "border-box",
                fontFamily: "inherit",
                fontSize: "0.95rem",
                lineHeight: 1.55,
                color: "text.primary",
                padding: "10px 14px",
                border: "1px solid #c4c4c4",
                borderRadius: "4px",
                backgroundColor: "#fff",
                resize: "vertical",
                outline: "none",
                transition: "border-color 0.15s",
                "&:hover": { borderColor: "rgba(0, 0, 0, 0.6)" },
                "&:focus": { borderColor: "#1976d2" },
                "&::placeholder": { color: "rgba(0, 0, 0, 0.42)" },
              }}
            />
            <Typography sx={FIELD_HELPER_SX}>
              Shown on article cards and at the top of the published page.
            </Typography>
          </Box>

          <Stack
            direction={{ xs: "column", md: "row" }}
            spacing={2.5}
            alignItems={{ md: "flex-start" }}
          >
            <Box>
              <Typography sx={SECTION_LABEL_SX}>Category</Typography>
              <TextField
                select
                size="small"
                value={state.fields.categoryId ?? ""}
                onChange={(e) =>
                  editor.setField("categoryId", e.target.value || null)
                }
                sx={{ ...INPUT_SX, minWidth: 240 }}
              >
                <MenuItem value="">
                  <em>None</em>
                </MenuItem>
                {categories.map((c) => (
                  <MenuItem key={c.id} value={c.id}>
                    {c.name}
                  </MenuItem>
                ))}
              </TextField>
            </Box>

            {isDraft && (
              <Box>
                <Typography sx={SECTION_LABEL_SX}>Visibility</Typography>
                <Box sx={{ pt: 0.25 }}>
                  <DraftVisibilityToggle
                    value={state.fields.visibility}
                    onChange={(v: ResearchArticleVisibility) =>
                      editor.setField("visibility", v)
                    }
                    disabled={isBusy}
                  />
                </Box>
              </Box>
            )}
          </Stack>
        </Stack>
      </Box>

      <MarkdownEditor
        heading="Body"
        value={state.fields.bodyMd}
        onChange={(v) => editor.setField("bodyMd", v)}
        minRows={24}
        stickyToolbarTop={helperStickyTop}
      />

      {/* Embeds + related articles. Two render paths:
            - Existing article: API-driven editors (immediate save on
              each op; mutations don't bump `current_version`).
            - New article: pending editors backed by the reducer's
              `pendingEmbeds` / `pendingRelated`; the create POST
              ships them with the article body in one transaction so
              authors don't have to save-then-edit. */}
      {isExisting && slug ? (
        <>
          <EmbeddedQueriesList slug={slug} />
          <RelatedArticlesEditor slug={slug} />
        </>
      ) : (
        <>
          <PendingEmbedsList embeds={state.pendingEmbeds} editor={editor} />
          <PendingRelatedList
            related={state.pendingRelated}
            editor={editor}
          />
        </>
      )}

      {isExisting && (
        <Box sx={{ ...METADATA_CARD_SX, p: 2 }}>
          <Typography sx={SECTION_LABEL_SX}>Change note (optional)</Typography>
          <TextField
            fullWidth
            size="small"
            placeholder="What did you change?"
            value={state.changeNote}
            onChange={(e) => editor.setChangeNote(e.target.value)}
            sx={INPUT_SX}
          />
          <Typography sx={FIELD_HELPER_SX}>
            Attached to the version snapshot — handy for skimming history
            later.
          </Typography>
        </Box>
      )}

      {isBusy && (
        <Box sx={{ display: "flex", alignItems: "center", gap: 1, mt: 1 }}>
          <CircularProgress size={16} />
          <Typography variant="body2" color="text.secondary">
            {state.status === "saving" && "Saving…"}
            {state.status === "publishing" && "Publishing…"}
            {state.status === "unpublishing" && "Unpublishing…"}
            {state.status === "deleting" && "Deleting…"}
          </Typography>
        </Box>
      )}

      <VersionHistoryDrawer
        open={versionDrawerOpen}
        onClose={() => setVersionDrawerOpen(false)}
        slug={isExisting ? slug ?? null : null}
      />
    </Box>
  );
}
